<template lang="pug">
mixin packageGrid
  .package-grid
    button.package-option(
      v-for="item in packageOptions"
      :key="item.quota_gb"
      type="button"
      :class="{ 'is-active': Number(pricing.extraPremium) === item.quota_gb }"
      :aria-pressed="String(Number(pricing.extraPremium) === item.quota_gb)"
      @click="pricing.extraPremium = item.quota_gb"
    )
      strong {{ item.quota_gb }} GB
      span €{{ formatCents(item.price_eur_cents) }}
      Check.package-check(v-if="Number(pricing.extraPremium) === item.quota_gb" :size="16" aria-hidden="true")

mixin perGbPurchase
  .quantity-field(v-if="canBuyExtraForThisMonth && !pricing.allPre")
    .quantity-head
      span.field-label {{ $t('pricing.to_buy') }}
      .quantity-value
        input.quantity-input(
          type="number"
          v-model.number="pricing.extraPremium"
          :min="minPurchaseGb"
          :max="isRelayAddon ? undefined : maxPreForCurrentMonth"
          step="1"
          :disabled="pricing.allPre"
          :aria-label="$t('pricing.to_buy')"
        )
        span.quantity-unit GB
    Slider.quantity-slider(v-if="!isRelayAddon" v-model="sliderExtraPremium" :max="maxPreForCurrentMonth" :min="minPurchaseGb" :step="1" :disabled="pricing.allPre")
    .quantity-range(v-if="!isRelayAddon" aria-hidden="true")
      span {{ minPurchaseGb }} GB
      span {{ maxPreForCurrentMonth }} GB
  p.offer-meta(v-if="canBuyExtraForThisMonth && !pricing.allPre")
    span.offer-price {{ $t('pricing.per_gb_price', { n: formatCents(currentOffer.unitPriceCents) }) }}
    template(v-if="isRelayAddon && currentOffer.fullQuotaGb && !relayGiftClaimed")
      span.offer-sep(aria-hidden="true") ·
      span(v-if="gbToFullQuota === 0") {{ $t('pricing.relay_full_quota_after_purchase', { quota: currentOffer.fullQuotaGb }) }}
      span(v-else) {{ $t('pricing.relay_more_to_full_quota', { n: gbToFullQuota, quota: currentOffer.fullQuotaGb }) }}
    //- Academic plan: upgrade link next to the price, with what Pro relay
    //- pricing would give — no remaining-allowance line here.
    template(v-if="currentService?.plan_id === 2")
      span.offer-sep(aria-hidden="true") ·
      PLink.offer-upgrade(@click="props.upgradePlan" type="success") {{ $t('service_details.upgrade') }}
      span.offer-benefit(v-if="proRelayOffer.ok && proRelayOffer.fullQuotaGb") {{ $t('pricing.upgrade_relay_benefit', { n: formatCents(proRelayOffer.unitPriceCents), threshold: proRelayOffer.periodPurchaseLimitGb, quota: proRelayOffer.fullQuotaGb }) }}
  .all-periods(v-if="showAllPeriodsOption")
    p.all-periods-note(v-if="currentAllPre > 0") {{ $t('pricing.already_unlimited_for', { n: currentAllPre }) }}
    label.all-periods-option(for="all_pre_checkbox" :class="{ 'is-active': pricing.allPre }")
      Checkbox#all_pre_checkbox.all-periods-check(v-model="pricing.allPre")
      span.all-periods-text(v-if="currentAllPre === 0") {{ $t('pricing.get_extra_for', { n: currentService?.extra?.buy_length - currentAllPre, quota: currentOffer.fullQuotaGb, price: formatCents(currentOffer.periodPurchaseLimitGb * currentOffer.unitPriceCents) }) }}
      span.all-periods-text(v-else) {{ $t('pricing.get_extra_for_all', { n: currentService?.extra?.buy_length - currentAllPre, quota: currentOffer.fullQuotaGb, price: formatCents(currentOffer.periodPurchaseLimitGb * currentOffer.unitPriceCents) }) }}
      Sparkles.all-periods-icon(:size="18" aria-hidden="true")

.extra-premium
  .addon-policy-state(v-if="dataAddonPricingPending") {{ $t('pricing.data_addon_loading') }}
  .addon-policy-state.is-error(v-else-if="dataAddonPolicyUnavailable") {{ $t('pricing.data_addon_unavailable') }}
  template(v-else)
    .addon-product-grid(v-if="showProductChoices")
      .addon-product-card(:class="{ 'is-active': currentProduct === RELAY_DATA_ADDON_PLAN_IDENTIFIER, 'is-disabled': !relayServiceEligible }")
        button.addon-product-head(
          type="button"
          :disabled="!relayServiceEligible"
          :aria-expanded="String(currentProduct === RELAY_DATA_ADDON_PLAN_IDENTIFIER)"
          @click="selectProduct(RELAY_DATA_ADDON_PLAN_IDENTIFIER)"
        )
          .addon-product-titles
            strong {{ $t('pricing.relay_data_addon') }}
            span(v-if="relayOffer.ok") {{ $t('pricing.pay_per_gb_summary', { n: formatCents(relayOffer.unitPriceCents) }) }}
          ChevronDown.addon-product-chevron(:size="20" aria-hidden="true")
        .addon-product-body(v-if="currentProduct === RELAY_DATA_ADDON_PLAN_IDENTIFIER")
          template(v-if="currentService && canBuyExtraPremiumForOtherMonth")
            +perGbPurchase
          p.addon-policy-state(v-else) {{ selectedProductUnavailableMessage }}
      .addon-product-card(:class="{ 'is-active': currentProduct === ALLPLATFORM_QUOTE_PLAN_IDENTIFIER, 'is-disabled': !standardServiceEligible }")
        button.addon-product-head(
          type="button"
          :disabled="!standardServiceEligible"
          :aria-expanded="String(currentProduct === ALLPLATFORM_QUOTE_PLAN_IDENTIFIER)"
          @click="selectProduct(ALLPLATFORM_QUOTE_PLAN_IDENTIFIER)"
        )
          .addon-product-titles
            strong {{ $t('pricing.standard_data_addon') }}
            span(v-if="standardOffer.ok && standardOffer.kind === 'packages'") {{ $t('pricing.prepaid_package_summary', { list: packageSummary }) }}
          ChevronDown.addon-product-chevron(:size="20" aria-hidden="true")
        .addon-product-body(v-if="currentProduct === ALLPLATFORM_QUOTE_PLAN_IDENTIFIER")
          template(v-if="isPackageAddon && canBuyExtraPremiumForOtherMonth")
            +packageGrid
          p.addon-policy-state(v-else) {{ selectedProductUnavailableMessage }}
    template(v-else)
      .package-picker(v-if="isPackageAddon && canBuyExtraPremiumForOtherMonth")
        span.field-label {{ $t('pricing.choose_standard_data_package') }}
        +packageGrid
      div(v-else-if="currentService && canBuyExtraPremiumForOtherMonth")
        +perGbPurchase
      div(v-else)
        h2.subheader {{ selectedProductUnavailableMessage }}

    p.addon-validation(v-if="quoteErrorMessage") {{ quoteErrorMessage }}

  Separator(class="my-4")
  .explain
    ul
      template(v-if="currentProduct === ALLPLATFORM_QUOTE_PLAN_IDENTIFIER")
        li {{ $t('pricing.standard_data_package_explain') }}
      template(v-else-if="isHomeDataAddon")
        li {{ $t('pricing.home_data.explain.a') }}
        li {{ $t('pricing.home_data.explain.b') }}
      template(v-else)
        li(v-if="currentOffer.ok && currentOffer.fullQuotaGb") {{ $t('pricing.relay_full_quota_hint', { threshold: currentOffer.periodPurchaseLimitGb, quota: currentOffer.fullQuotaGb }) }}
        li {{ $t('pricing.extra_premium_explain.d') }}
</template>

<script lang="ts" setup>
import { Check, ChevronDown, Sparkles } from 'lucide-vue-next'
import { Checkbox } from '@/components/ui/checkbox'
import { Slider } from '@/components/ui/slider'
import { Separator } from '@/components/ui/separator'
import {
  ALLPLATFORM_QUOTE_PLAN_IDENTIFIER,
  RELAY_DATA_ADDON_PLAN_IDENTIFIER,
  isDataAddonPricingPending,
  isDataAddonServiceEligible,
  relayDataAddonPeriodRefreshDelayMs,
  relayDataAddonPeriodWatchKey,
  resolveDataAddonProductForService,
  resolveDataAddonOffer,
  type DataAddonProductIdentifier,
} from '@/utils/dataAddonPricing'
const props = defineProps<{
  upgradePlan: any,
  // Pin the current data-addon product (dialog entries pick one per button);
  // hides the relay/standard product switcher.
  lockProduct?: boolean,
}>()
const pricing = usePricing()
const { t: $t } = useI18n()
const relayPeriodClock = ref(Date.now())
let relayPeriodRefreshTimer: ReturnType<typeof setTimeout> | null = null

function refreshRelayPeriodClock() {
  const now = Date.now()
  relayPeriodClock.value = now
  relayPeriodRefreshTimer = setTimeout(
    refreshRelayPeriodClock,
    relayDataAddonPeriodRefreshDelayMs(now),
  )
}

onMounted(refreshRelayPeriodClock)
onBeforeUnmount(() => {
  if (relayPeriodRefreshTimer != null) clearTimeout(relayPeriodRefreshTimer)
})

const sliderExtraPremium = computed({
  get: () => {
    return [pricing.extraPremium]
  },
  set(value) {
    pricing.extraPremium = Number(value[0])
  }
})

const currentService = computed(() => pricing.currentService)
const isHomeDataAddon = computed(() => pricing.cate === 'home')
const currentProduct = computed(() => pricing.currentDataAddonProduct)
const dataAddonPricingPending = computed(() =>
  isDataAddonPricingPending(pricing.dataAddonPricingStatus))
const offerPlanId = computed(() =>
  Number(currentService.value?.plan_id || pricing.dataAddonPlanId))
const currentOffer = computed(() => resolveDataAddonOffer(
  pricing.dataAddonPolicies,
  offerPlanId.value,
  currentProduct.value,
))
const relayOffer = computed(() => resolveDataAddonOffer(
  pricing.dataAddonPolicies,
  offerPlanId.value,
  RELAY_DATA_ADDON_PLAN_IDENTIFIER,
))
const standardOffer = computed(() => resolveDataAddonOffer(
  pricing.dataAddonPolicies,
  offerPlanId.value,
  ALLPLATFORM_QUOTE_PLAN_IDENTIFIER,
))
const dataAddonPolicyUnavailable = computed(() =>
  pricing.dataAddonPricingStatus === 'error')
const relayServiceEligible = computed(() =>
  isDataAddonServiceEligible(
    currentService.value,
    RELAY_DATA_ADDON_PLAN_IDENTIFIER,
    relayPeriodClock.value,
  ))
const standardServiceEligible = computed(() =>
  isDataAddonServiceEligible(
    currentService.value,
    ALLPLATFORM_QUOTE_PLAN_IDENTIFIER,
    relayPeriodClock.value,
  ))
const showProductChoices = computed(() =>
  !props.lockProduct && offerPlanId.value === 4 && relayOffer.value.ok && standardOffer.value.ok)
const isRelayAddon = computed(() => currentProduct.value === RELAY_DATA_ADDON_PLAN_IDENTIFIER)
const isPackageAddon = computed(() => currentOffer.value.ok && currentOffer.value.kind === 'packages')
const packageOptions = computed(() => isPackageAddon.value ? currentOffer.value.packages : [])
const packageSummary = computed(() => {
  if (!standardOffer.value.ok || standardOffer.value.kind !== 'packages') return ''
  return standardOffer.value.packages
    .map(item => `${item.quota_gb}GB`)
    .join(' / ')
})
const formatCents = (cents: number) => (Number(cents) / 100).toFixed(2)
const selectedProductUnavailableMessage = computed(() => $t(
  'pricing.selected_data_addon_unavailable',
  {
    product: $t(currentProduct.value === RELAY_DATA_ADDON_PLAN_IDENTIFIER
      ? 'pricing.relay_data_addon'
      : 'pricing.standard_data_addon'),
  },
))

const currentAllPre = computed(() => {
  return Number(currentService.value?.extra?.all_pre || 0)
})
const canBuyExtraPremiumForOtherMonth = computed(() => {
  if (isPackageAddon.value) {
    return Number(currentService.value?.plan_id) === 4 && isDataAddonServiceEligible(currentService.value, ALLPLATFORM_QUOTE_PLAN_IDENTIFIER, relayPeriodClock.value)
  }
  if (isHomeDataAddon.value) return [5, 6].includes(Number(currentService.value?.plan_id))
  return relayServiceEligible.value
})
const canBuyExtraForThisMonth = computed(() => {
  if (!currentOffer.value.ok || currentOffer.value.kind !== 'per_gb') return false
  if (isHomeDataAddon.value) return [5, 6].includes(Number(currentService.value?.plan_id))
  return relayServiceEligible.value && pricing.alreadyBoughtReadyForCurrentPeriod
})
const maxPreForCurrentMonth = computed(() => {
  return pricing.maxPreForCurrentMonth
})
const minPurchaseGb = computed(() => currentOffer.value.ok && currentOffer.value.kind === 'per_gb'
  ? currentOffer.value.minPurchaseGb
  : 1)
const relayGiftClaimed = computed(() => Number(currentAllPre.value) > 0
  || (currentOffer.value.ok && currentOffer.value.kind === 'per_gb'
    && Number(pricing.alreadyBought.allplatform || 0) >= Number(currentOffer.value.periodPurchaseLimitGb)))
const gbToFullQuota = computed(() => currentOffer.value.ok && currentOffer.value.kind === 'per_gb'
  ? Math.max(0, Number(currentOffer.value.periodPurchaseLimitGb) - Number(pricing.alreadyBought.allplatform || 0) - (Number(pricing.extraPremium) || 0))
  : 0)
// Pro relay terms, shown to academic-plan users as the reason to upgrade.
const proRelayOffer = computed(() => resolveDataAddonOffer(
  pricing.dataAddonPolicies,
  4,
  RELAY_DATA_ADDON_PLAN_IDENTIFIER,
))
const showAllPeriodsOption = computed(() => isRelayAddon.value
  && currentOffer.value.ok
  && Boolean(currentOffer.value.fullQuotaGb)
  && Number(currentService.value?.extra?.buy_length || 0) > currentAllPre.value)
watch([showAllPeriodsOption, dataAddonPricingPending], ([available, pending]) => {
  if (!pending && !available) pricing.allPre = false
}, { immediate: true })
const quoteErrorMessage = computed(() => {
  if (dataAddonPricingPending.value) return $t('pricing.data_addon_loading')
  const quote = pricing.dataAddonQuote
  if (quote.ok) return ''
  // 0 / blank / non-integer input is 'invalid_quantity', not 'below_minimum';
  // both mean "type at least the minimum" for a per-GB offer.
  if (['below_minimum', 'invalid_quantity'].includes(quote.reason) && currentOffer.value.ok && currentOffer.value.kind === 'per_gb') {
    return $t('pricing.data_addon_minimum', { n: currentOffer.value.minPurchaseGb })
  }
  if (quote.reason === 'above_maximum') {
    return $t('pricing.data_addon_maximum', { n: maxPreForCurrentMonth.value })
  }
  if (['package_unavailable', 'invalid_quantity'].includes(quote.reason)) return $t('pricing.choose_standard_data_package')
  if (quote.detail === 'inactive_service') return ''
  return $t('pricing.data_addon_unavailable')
})

function selectProduct(product: DataAddonProductIdentifier) {
  pricing.selectDataAddonProduct(product)
}

watch(
  () => [
    pricing.dataAddonPricingStatus,
    currentService.value?.id,
    currentService.value?.status,
    currentProduct.value,
  ],
  () => {
    if (pricing.dataAddonPricingStatus !== 'ready') return
    const resolvedProduct = resolveDataAddonProductForService(
      currentService.value,
      currentProduct.value,
      relayPeriodClock.value,
    )
    if (resolvedProduct !== currentProduct.value) {
      pricing.selectDataAddonProduct(resolvedProduct)
    }
  },
  { immediate: true },
)

watch(() => pricing.extraPremium, (val) => {
  if (!isPackageAddon.value && !isRelayAddon.value && val > maxPreForCurrentMonth.value && maxPreForCurrentMonth.value > 0) {
    pricing.extraPremium = maxPreForCurrentMonth.value
  }
}, { immediate: true })
watch(
  () => [currentService.value?.plan_id, pricing.dataAddonPricingStatus, currentProduct.value],
  () => {
    const quote = pricing.dataAddonQuote
    if (currentOffer.value.ok && !quote.ok && ['invalid_quantity', 'below_minimum', 'package_unavailable'].includes(quote.reason)) {
      pricing.setDefaultDataAddonQuantity()
    }
  },
  { immediate: true },
)
watch(
  () => relayDataAddonPeriodWatchKey(currentService.value, relayPeriodClock.value),
  async () => { await pricing.loadAlreadyBought() },
  { immediate: true },
)

</script>

<style lang="postcss" scoped>
.extra-premium {
  .addon-policy-state,
  .addon-validation {
    margin: 0.75rem 0;
    text-align: center;
    color: var(--pricing-muted);
    font-size: 0.85rem;
    line-height: 1.5;
  }

  .addon-policy-state.is-error,
  .addon-validation {
    color: #d97706;
  }

  .addon-validation {
    margin: 0.6rem 0 0;
    text-align: left;
    font-size: 0.8rem;
  }

  .addon-product-grid {
    display: grid;
    gap: 0.75rem;
    margin: 0.75rem 0 1.25rem;
  }

  .package-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
  }

  .package-picker {
    display: grid;
    gap: 0.6rem;
    margin-top: 0.25rem;
  }

  /* Shared small label above a control (quantity, package list). */
  .field-label {
    display: block;
    color: var(--pricing-muted);
    font-size: 0.85rem;
    font-weight: 600;
    line-height: 1.2;
  }

  .addon-product-card {
    min-width: 0;
    border: 1px solid rgba(126, 211, 33, 0.2);
    border-radius: 0.85rem;
    background: rgba(255, 255, 255, 0.03);
  }

  .addon-product-card:not(.is-disabled):hover,
  .addon-product-card.is-active {
    border-color: var(--logo-green);
    box-shadow: 0 0 0 2px rgba(126, 211, 33, 0.12);
  }

  .addon-product-card.is-disabled {
    opacity: 0.5;
  }

  .addon-product-head {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.9rem 1rem;
    background: none;
    border: 0;
    color: inherit;
    text-align: left;
    cursor: pointer;
  }

  .addon-product-head:disabled {
    cursor: not-allowed;
  }

  .addon-product-head:focus-visible {
    outline: 2px solid var(--logo-green);
    outline-offset: -2px;
    border-radius: 0.85rem;
  }

  .addon-product-titles {
    display: grid;
    gap: 0.35rem;
  }

  .addon-product-chevron {
    flex-shrink: 0;
    color: var(--secondary-color);
    transition: transform 0.2s ease, color 0.2s ease;
  }

  .addon-product-card.is-active .addon-product-chevron {
    transform: rotate(180deg);
    color: var(--logo-green);
  }

  .addon-product-body {
    margin: 0 1rem;
    padding: 1rem 0;
    border-top: 1px solid rgba(126, 211, 33, 0.15);
  }

  .package-option {
    display: flex;
    align-items: baseline;
    gap: 0.5rem;
    min-width: 0;
    padding: 0.9rem 1.4rem;
    border: 1px solid rgba(126, 211, 33, 0.2);
    border-radius: 0.85rem;
    background: rgba(255, 255, 255, 0.03);
    color: inherit;
    text-align: left;
    cursor: pointer;
  }

  .package-option:hover,
  .package-option:focus-visible,
  .package-option.is-active {
    border-color: var(--logo-green);
    outline: none;
    box-shadow: 0 0 0 2px rgba(126, 211, 33, 0.12);
  }

  .addon-product-titles span,
  .package-option span {
    color: var(--secondary-color);
    font-size: 0.82rem;
  }

  .package-check {
    align-self: center;
    color: var(--logo-green);
  }

  /* Quantity: label + focal number on one line, slider underneath, bounds below. */
  .quantity-field {
    display: grid;
    gap: 0.6rem;
    margin-top: 0.25rem;
  }

  .quantity-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
  }

  .quantity-value {
    display: inline-flex;
    align-items: baseline;
    gap: 0.4rem;
  }

  /* Visibly an input — customers can type the amount, not only drag. */
  .quantity-input {
    width: 5.5ch;
    padding: 0.15rem 0.5rem;
    border: 1px solid var(--pricing-border);
    border-radius: 0.6rem;
    background: var(--pricing-surface-strong);
    color: var(--pricing-text);
    font-size: 1.6rem;
    font-weight: 700;
    line-height: 1.2;
    text-align: right;
    font-variant-numeric: tabular-nums;
    -moz-appearance: textfield;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    &:hover:not(:disabled) {
      border-color: var(--pricing-accent-line);
    }

    &:focus-visible {
      outline: none;
      border-color: var(--pricing-accent);
      box-shadow: 0 0 0 3px var(--pricing-accent-soft);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  .quantity-unit {
    color: var(--pricing-muted);
    font-size: 0.95rem;
    font-weight: 600;
  }

  .quantity-slider {
    padding: 0.35rem 0;
  }

  .quantity-range {
    display: flex;
    justify-content: space-between;
    color: var(--pricing-muted);
    font-size: 0.75rem;
    line-height: 1.2;
    font-variant-numeric: tabular-nums;
  }

  /* Unit price · relay nudge · optional upgrade link + benefit, one muted line. */
  .offer-meta {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 0.2rem 0.45rem;
    margin: 0.9rem 0 0;
    color: var(--pricing-muted);
    font-size: 0.8rem;
    line-height: 1.45;
  }

  .offer-price {
    color: var(--pricing-text);
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }

  .offer-sep {
    opacity: 0.6;
  }

  .offer-upgrade {
    font-weight: 600;
  }

  /* Full-quota upsell: a selectable row that reads as the featured offer
     (brand tint + border), deepening once picked. */
  .all-periods {
    display: grid;
    gap: 0.5rem;
    margin-top: 1.1rem;
  }

  .all-periods-note {
    margin: 0;
    color: var(--pricing-muted);
    font-size: 0.8rem;
    line-height: 1.45;
  }

  .all-periods-option {
    display: flex;
    align-items: flex-start;
    gap: 0.7rem;
    padding: 0.8rem 0.95rem;
    border: 1px solid var(--pricing-accent-line);
    border-radius: 0.85rem;
    background: var(--pricing-accent-soft);
    color: var(--pricing-text);
    cursor: pointer;
    transition: border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
  }

  .all-periods-option:hover {
    border-color: var(--pricing-accent);
    background: color-mix(in srgb, var(--pricing-accent) 14%, transparent);
  }

  .all-periods-option.is-active {
    border-color: var(--pricing-accent);
    background: color-mix(in srgb, var(--pricing-accent) 18%, transparent);
    box-shadow: 0 0 0 2px color-mix(in srgb, var(--pricing-accent) 20%, transparent);
  }

  .all-periods-check {
    margin-top: 0.15rem;
  }

  .all-periods-text {
    flex: 1;
    min-width: 0;
    font-size: 0.9rem;
    font-weight: 600;
    line-height: 1.4;
  }

  .all-periods-icon {
    flex: none;
    margin-top: 0.05rem;
    color: var(--pricing-accent);
  }

  /* Fine print. */
  .explain {
    text-align: left;

    ul {
      display: grid;
      gap: 0.35rem;
      margin: 0;
      padding-left: 1.1rem;
      list-style: disc;
      color: var(--pricing-muted);
      font-size: 0.78rem;
      line-height: 1.55;
    }
  }

  @media (max-width: 768px) {
    .quantity-input {
      font-size: 1.35rem;
    }
  }
}

.hint-for-basic {
  margin-bottom: 2rem;

  h5 {
    font-weight: normal;
    color: var(--secondary-color);
  }
}
</style>


<style lang="postcss">
.extra-premium {
  .all-pre-check {
    box-shadow: var(--shadow-medium);
    margin-top: 1rem;
    padding: 1.2rem 2rem;
    background: var(--primary-color);
    border-radius: 2rem;

    b {
      color: var(--revert-primary);
    }
  }
}
</style>
